"use client";

import { useState, useCallback, useEffect } from "react";
import { Search, Filter, ChevronDown, Loader2, Plus, X, Tag, Rss, Sparkles } from "lucide-react";
import { PaperCard, type PaperCardProps } from "@/components/papers/PaperCard";
import { SUBSPECIALTIES } from "@/lib/constants";
import { usePaperBookmarks } from "@/lib/paper-bookmarks";

const ARTICLE_TYPES = [
  { id: "all", label: "전체" },
  { id: "meta-analysis", label: "Meta-analysis" },
  { id: "guideline", label: "Guideline" },
  { id: "rct", label: "RCT" },
  { id: "review", label: "Review" },
  { id: "original", label: "Original" },
  { id: "case-report", label: "Case Report" },
];

interface PaperWithMapping extends PaperCardProps {
  diseaseSlug?: string;
  subspecialty?: string;
}

interface PapersClientProps {
  initialPapers: PaperWithMapping[];
  total: number;
  stats: { subspecialty: string; count: number }[];
  pageSize: number;
}

async function fetchPapers(params: {
  limit: number;
  offset: number;
  search?: string;
  articleType?: string;
  subspecialties?: string[];
  recentlyAdded?: boolean;
}): Promise<{ papers: PaperWithMapping[]; total: number }> {
  const sp = new URLSearchParams();
  sp.set("limit", String(params.limit));
  sp.set("offset", String(params.offset));
  if (params.search) sp.set("search", params.search);
  if (params.articleType && params.articleType !== "all") sp.set("articleType", params.articleType);
  if (params.subspecialties && params.subspecialties.length > 0) {
    sp.set("subspecialty", params.subspecialties.join(","));
  }
  if (params.recentlyAdded) sp.set("recentlyAdded", "1");
  const res = await fetch(`/api/papers?${sp}`);
  return res.json();
}

// ── 키워드 관련 타입 ──
interface UserKeyword {
  id: string;
  keyword: string;
  createdAt: string;
}

interface FeedPaper extends PaperCardProps {
  matchScore?: number;
}

export function PapersClient({
  initialPapers,
  total: initialTotal,
  stats,
  pageSize,
}: PapersClientProps) {
  const [tab, setTab] = useState<"all" | "recent" | "feed">("all");
  const [recentPapers, setRecentPapers] = useState<PaperWithMapping[]>([]);
  const [recentTotal, setRecentTotal] = useState(0);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentLoadingMore, setRecentLoadingMore] = useState(false);
  const [recentLoaded, setRecentLoaded] = useState(false);
  const [papers, setPapers] = useState<PaperWithMapping[]>(initialPapers);
  const [total, setTotal] = useState(initialTotal);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedSubs, setSelectedSubs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { isPaperBookmarked, togglePaperBookmark, isLoggedIn } = usePaperBookmarks();

  // ── 키워드 피드 state ──
  const [keywords, setKeywords] = useState<UserKeyword[]>([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [feedPapers, setFeedPapers] = useState<FeedPaper[]>([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);
  const [keywordsLoaded, setKeywordsLoaded] = useState(false);

  // 키워드 로드 (탭 전환 시)
  useEffect(() => {
    if (tab === "feed" && isLoggedIn && !keywordsLoaded) {
      loadKeywords();
    }
  }, [tab, isLoggedIn, keywordsLoaded]);

  // 이번달 신규 로드 (탭 전환 시 1회)
  useEffect(() => {
    if (tab === "recent" && !recentLoaded) {
      loadRecent();
    }
  }, [tab, recentLoaded]);

  async function loadRecent(offset = 0) {
    if (offset === 0) setRecentLoading(true);
    else setRecentLoadingMore(true);
    try {
      const result = await fetchPapers({
        limit: pageSize,
        offset,
        recentlyAdded: true,
      });
      if (offset === 0) {
        setRecentPapers(result.papers);
        setRecentLoaded(true);
      } else {
        setRecentPapers((prev) => [...prev, ...result.papers]);
      }
      setRecentTotal(result.total);
    } finally {
      setRecentLoading(false);
      setRecentLoadingMore(false);
    }
  }

  async function loadKeywords() {
    try {
      const res = await fetch("/api/keywords");
      if (res.ok) {
        const data = await res.json();
        setKeywords(data);
        setKeywordsLoaded(true);
        if (data.length > 0) loadFeed();
      }
    } catch {
      // ignore
    }
  }

  async function loadFeed(offset = 0) {
    if (offset === 0) setFeedLoading(true);
    else setFeedLoadingMore(true);
    try {
      const res = await fetch(`/api/keywords/feed?limit=30&offset=${offset}`);
      const data = await res.json();
      if (offset === 0) {
        setFeedPapers(data.papers);
      } else {
        setFeedPapers((prev) => [...prev, ...data.papers]);
      }
      setFeedTotal(data.total);
    } finally {
      setFeedLoading(false);
      setFeedLoadingMore(false);
    }
  }

  async function addKeyword() {
    if (!newKeyword.trim()) return;
    const res = await fetch("/api/keywords", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keyword: newKeyword.trim() }),
    });
    if (res.ok) {
      const kw = await res.json();
      setKeywords((prev) => [...prev, kw]);
      setNewKeyword("");
      // 피드 새로고침
      loadFeed();
    }
  }

  async function removeKeyword(id: string) {
    await fetch("/api/keywords", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    setKeywords((prev) => prev.filter((k) => k.id !== id));
    // 키워드가 남아있으면 피드 새로고침
    const remaining = keywords.filter((k) => k.id !== id);
    if (remaining.length > 0) {
      // 약간의 딜레이 후 새로고침
      setTimeout(() => loadFeed(), 100);
    } else {
      setFeedPapers([]);
      setFeedTotal(0);
    }
  }

  // ── 전체 논문 탭 로직 (기존) ──
  const applyFilters = useCallback(
    async (newSearch: string, newType: string, subs: Set<string>) => {
      setLoading(true);
      try {
        const result = await fetchPapers({
          limit: pageSize,
          offset: 0,
          search: newSearch || undefined,
          articleType: newType,
          subspecialties: subs.size > 0 ? Array.from(subs) : undefined,
        });
        setPapers(result.papers);
        setTotal(result.total);
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  let searchTimeout: ReturnType<typeof setTimeout>;
  function handleSearchChange(value: string) {
    setSearch(value);
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      applyFilters(value, selectedType, selectedSubs);
    }, 400);
  }

  function handleTypeChange(type: string) {
    setSelectedType(type);
    applyFilters(search, type, selectedSubs);
  }

  function handleSubToggle(sub: string) {
    setSelectedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(sub)) next.delete(sub);
      else next.add(sub);
      applyFilters(search, selectedType, next);
      return next;
    });
  }

  function clearSubFilter() {
    setSelectedSubs(new Set());
    applyFilters(search, selectedType, new Set());
  }

  async function loadMore() {
    setLoadingMore(true);
    try {
      const result = await fetchPapers({
        limit: pageSize,
        offset: papers.length,
        search: search || undefined,
        articleType: selectedType,
        subspecialties: selectedSubs.size > 0 ? Array.from(selectedSubs) : undefined,
      });
      setPapers((prev) => [...prev, ...result.papers]);
    } finally {
      setLoadingMore(false);
    }
  }

  const hasMore = papers.length < total;
  const feedHasMore = feedPapers.length < feedTotal;

  return (
    <div className="space-y-6">
      {/* 탭 전환 */}
      <div className="flex gap-1 rounded-lg border bg-muted/50 p-1 w-fit">
        <button
          onClick={() => setTab("all")}
          className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "all"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Search className="h-3.5 w-3.5" />
          전체 논문
        </button>
        <button
          onClick={() => setTab("feed")}
          className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "feed"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Rss className="h-3.5 w-3.5" />
          내 키워드 피드
          {keywords.length > 0 && (
            <span className="ml-1 rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-bold">
              {keywords.length}
            </span>
          )}
        </button>
      </div>

      {/* ═══ 전체 논문 탭 ═══ */}
      {tab === "all" && (
        <>
          {/* Stats overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            <div
              className={`rounded-lg border bg-card p-3 text-center cursor-pointer transition-colors ${
                selectedSubs.size === 0
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "hover:border-primary/30"
              }`}
              onClick={clearSubFilter}
            >
              <div className="text-2xl font-bold text-foreground">{initialTotal}</div>
              <div className="text-xs text-muted-foreground">전체</div>
            </div>
            {SUBSPECIALTIES.map((sub) => {
              const s = stats.find((x) => x.subspecialty === sub.id);
              if (!s) return null;
              const isSelected = selectedSubs.has(s.subspecialty);
              return (
                <div
                  key={s.subspecialty}
                  className={`rounded-lg border bg-card p-3 text-center cursor-pointer transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "hover:border-primary/30"
                  }`}
                  onClick={() => handleSubToggle(s.subspecialty)}
                >
                  <div className="text-2xl font-bold text-foreground">{s.count}</div>
                  <div className="text-xs text-muted-foreground">{sub.name}</div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="논문 제목, 저널, 저자 검색..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <div className="flex flex-wrap gap-1">
                  {ARTICLE_TYPES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleTypeChange(t.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        selectedType === t.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {selectedSubs.size > 0 && (
                <button
                  onClick={clearSubFilter}
                  className="px-2.5 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                >
                  분과 필터 초기화
                </button>
              )}
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            {total}개 논문 중 {papers.length}개 표시
            {search && ` (검색: "${search}")`}
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                검색 중...
              </div>
            ) : papers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                조건에 맞는 논문이 없습니다
              </div>
            ) : (
              papers.map((p) => (
                <PaperCard
                  key={p.pmid}
                  {...p}
                  isBookmarked={isPaperBookmarked(p.pmid)}
                  onToggleBookmark={isLoggedIn ? togglePaperBookmark : undefined}
                />
              ))
            )}
          </div>

          {hasMore && !loading && (
            <div className="flex justify-center pt-2">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="flex items-center gap-2 rounded-lg border bg-card px-6 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    불러오는 중...
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    더보기 ({total - papers.length}개 남음)
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* ═══ 키워드 피드 탭 ═══ */}
      {tab === "feed" && (
        <>
          {!isLoggedIn ? (
            <div className="text-center py-16 text-muted-foreground">
              <Rss className="h-8 w-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm">로그인하면 관심 키워드 기반 논문 피드를 이용할 수 있습니다</p>
            </div>
          ) : (
            <>
              {/* 키워드 관리 */}
              <div className="rounded-xl border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">내 키워드</span>
                  <span className="text-xs text-muted-foreground">
                    (논문 title, abstract, MeSH terms에서 매칭)
                  </span>
                </div>

                {/* 키워드 목록 */}
                <div className="flex flex-wrap gap-2">
                  {keywords.map((kw) => (
                    <span
                      key={kw.id}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium"
                    >
                      {kw.keyword}
                      <button
                        onClick={() => removeKeyword(kw.id)}
                        className="rounded-full p-0.5 hover:bg-primary/20 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {keywords.length === 0 && (
                    <span className="text-xs text-muted-foreground">
                      키워드를 추가하면 관련 논문이 자동으로 표시됩니다
                    </span>
                  )}
                </div>

                {/* 키워드 추가 */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addKeyword()}
                    placeholder="키워드 입력 (예: OCT, RNFL, myopia, glaucoma)..."
                    className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <button
                    onClick={addKeyword}
                    disabled={!newKeyword.trim()}
                    className="flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    추가
                  </button>
                </div>
              </div>

              {/* 피드 결과 */}
              {keywords.length > 0 && (
                <>
                  <div className="text-sm text-muted-foreground">
                    키워드 매칭 논문 {feedTotal}개
                    {feedPapers.length > 0 && feedPapers.length < feedTotal &&
                      ` (${feedPapers.length}개 표시)`}
                  </div>

                  <div className="space-y-3">
                    {feedLoading ? (
                      <div className="flex items-center justify-center py-12 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        키워드 매칭 중...
                      </div>
                    ) : feedPapers.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        매칭되는 논문이 없습니다. 다른 키워드를 시도해보세요.
                      </div>
                    ) : (
                      feedPapers.map((p) => (
                        <PaperCard
                          key={p.pmid}
                          {...p}
                          isBookmarked={isPaperBookmarked(p.pmid)}
                          onToggleBookmark={isLoggedIn ? togglePaperBookmark : undefined}
                        />
                      ))
                    )}
                  </div>

                  {feedHasMore && !feedLoading && (
                    <div className="flex justify-center pt-2">
                      <button
                        onClick={() => loadFeed(feedPapers.length)}
                        disabled={feedLoadingMore}
                        className="flex items-center gap-2 rounded-lg border bg-card px-6 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                      >
                        {feedLoadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            불러오는 중...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4" />
                            더보기 ({feedTotal - feedPapers.length}개 남음)
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
